<template>
  <div class="mc-table-history">
    <div class="mc-table-history__filter">
      <span>{{ $t('delivery.loadsTable.history.activityLabel') }}:</span>
      <McInlineFilter
        :activeKeys="activeFilters"
        :filterItems="filterItems"
        :isMultiple="false"
        @update:activeKeys="handleFilterChange"
      />
    </div>
    <McTable
      ref="historyTable"
      :hasCheckbox="false"
      :fields="fields"
      :apiClient="apiClient"
      searchable
      :withCard="false"
    >
      <template #cell(created_on)="{ value }">
        <div>{{ formatIndexDate(value).date }}</div>
        <div class="mc-datetime">
          {{ formatIndexDate(value).time }}
        </div>
      </template>
      <template #cell(activity)="{ value }">
        <McBadge
          :variant="value === 'pickup' ? 'success' : 'primary'"
          :text="getActivityLabel(value)"
          type="button"
        />
      </template>

      <template #cell(product_name)="{ item }">
        <div
          v-b-tooltip.right="item.product?.name?.length > 60 ? item.product?.name : null"
          class="td__overflow-ellipsis-three-lines"
        >
          {{ item.product_name ?? '-' }}
        </div>
        <div v-if="item.product_type" class="text-muted text-sm">
          {{ item.product_type }}
        </div>
      </template>

      <template #cell(reason)="{ item }">
        <div
          v-b-tooltip.left="item.reason?.length > 60 ? item.reason : null"
          class="td__overflow-ellipsis-three-lines"
        >
          {{ item.reason || '-' }}
        </div>
      </template>
    </McTable>
  </div>
</template>

<script>
import McTable from '@/components/McTable'
import McBadge from '@/components/McBadge'
import McInlineFilter from '@/components/McInlineFilter'
import { formatNumber } from '@/helpers/format-number.js'
import { formatDateWithTz, formatIndexDate } from '@/helpers/tzDate'
import * as historyApi from '@/services/rest-api/lib/delivery/history'
import filterMappings from '@/router/views/delivery/lib/constants/filter-mappings'

export default {
  name: 'TableHistory',
  components: {
    McTable,
    McBadge,
    McInlineFilter,
  },
  props: {
    deliveryId: {
      type: String,
      default: '',
    },
    isAdjustedPicked: {
      type: Boolean,
      default: false,
    },
    isAdjustedDelivered: {
      type: Boolean,
      default: false,
    },
    status: {
      type: String,
      default: '',
    },
  },
  data() {
    return {
      tooltips: {},
      activeFilters: [],
    }
  },
  computed: {
    filterItems() {
      return [
        { id: 'all', label: this.$t('delivery.loadsTable.history.filter.all') },
        { id: 'Ambil', label: this.$t('delivery.loadsTable.history.filter.pickup') },
        { id: 'Kirim', label: this.$t('delivery.loadsTable.history.filter.delivery') },
      ]
    },
    fields() {
      return [
        {
          key: 'created_on',
          label: this.$t('delivery.loadsTable.history.fields.changeTime'),
          isSortable: false,
          thStyle: 'width: 150px',
          isFirstRowClickable: false,
        },
        {
          key: 'created_by',
          label: this.$t('delivery.loadsTable.history.fields.updatedBy'),
          isSortable: false,
          thStyle: 'width: 130px',
        },
        {
          key: 'activity',
          label: this.$t('delivery.loadsTable.history.fields.activity'),
          isSortable: false,
          thStyle: 'width: 100px',
        },
        {
          key: 'product_name',
          label: this.$t('delivery.loadsTable.history.fields.productName'),
          isSortable: false,
          thStyle: 'width: 150px',
        },
        {
          key: 'old_quantity',
          label: this.$t('delivery.loadsTable.history.fields.previousQuantity'),
          isSortable: false,
          thStyle: 'width: 130px',
        },
        {
          key: 'new_quantity',
          label: this.$t('delivery.loadsTable.history.fields.newQuantity'),
          isSortable: false,
          thStyle: 'width: 130px',
        },
        {
          key: 'reason',
          label: this.$t('delivery.loadsTable.history.fields.changeReason'),
          isSortable: false,
          thStyle: 'width: 200px',
        },
      ]
    },
    getEventFilterValue() {
      if (this.activeFilters.includes('Ambil')) {
        return 'FleetPlanning.DeliveryLoadUpdatedPicked'
      } else if (this.activeFilters.includes('Kirim')) {
        return 'FleetPlanning.DeliveryLoadUpdatedDropped'
      } else {
        return 'FleetPlanning.DeliveryLoadUpdatedPicked FleetPlanning.DeliveryLoadUpdatedDropped'
      }
    },
    apiClient() {
      const baseFilters = [
        {
          key: 'event:in',
          values: [this.getEventFilterValue],
        },
      ]

      const queryParamFilters = baseFilters.map(
        (filter) => filterMappings[filter.key]?.(filter.key, filter.values) ?? undefined
      )

      return {
        get: async (queryParam) => {
          const logResult = await historyApi.getLog(
            this.$route.params.id,
            queryParam,
            queryParamFilters
          )

          const keyMap = {
            product_name: 'product_name',
            product_type_name: 'product_type_name',
            uom: 'uom',
            old_qty: 'old_quantity',
            new_qty: 'new_quantity',
            reason: 'reason',
          }
          const remodeledLogData = logResult.data.paginated_result.map((log, index) => {
            const extractedData = {
              _rowIndex: index,
              created_by: log.created_by,
              created_on: log.created_on,
              product_name: '',
              product_type_name: '',
              product: {
                name: '',
                type: '',
              },
              uom: '',
              old_quantity: '',
              new_quantity: '',
              reason: '',
              activity: log.event.includes('Picked') ? 'pickup' : 'delivery',
            }

            log.details.forEach((item) => {
              if (keyMap[item.key]) {
                extractedData[keyMap[item.key]] = item.value
              }
            })
            extractedData.product = {
              name: extractedData.product_name,
              type: extractedData.product_type_name,
            }
            extractedData.old_quantity = `${extractedData.old_quantity} ${extractedData.uom}`
            extractedData.new_quantity = `${extractedData.new_quantity} ${extractedData.uom}`

            return extractedData
          })

          logResult.data.paginated_result = remodeledLogData

          return logResult
        },
      }
    },
  },
  methods: {
    formatDateWithTz,
    formatIndexDate,
    formatNumber(value) {
      return formatNumber(value) || '-'
    },
    getActivityLabel(value) {
      return value === 'pickup'
        ? this.$t('delivery.loadsTable.history.filter.pickup')
        : this.$t('delivery.loadsTable.history.filter.delivery')
    },
    handleFilterChange(newFilters) {
      this.activeFilters = newFilters
      this.$nextTick(() => {
        if (this.$refs.historyTable) {
          this.$refs.historyTable.getData({
            limit: this.$refs.historyTable.limit,
            page: this.$refs.historyTable.page,
          })
        }
      })
    },
    checkOverflow(event, id) {
      const element = event.target
      if (element.scrollHeight > element.clientHeight + 2) {
        this.$set(this.tooltips, id, false)
      } else {
        this.$set(this.tooltips, id, true)
      }
    },
  },
}
</script>
